import React, { useEffect, useState } from 'react';
import { useAuth, useT } from '@repo/common';

import ArticleCard from '../../components/Article/ArticleCard';
import AdminArticleGrid from '../../components/Article/Admin/AdminArticleGrid';
import {
  CONTAINER_CLASS,
  GRID_CLASS,
  SkeletonGrid,
  StateMessage,
} from '../../components/Article/articleFeedStyles';
import { getArticles } from '../../api/article/article.api';

/** JobBoard와 동일한 판정 기준 */
const ADMIN_ROLES = new Set(['A', 'ROLE_A', 'ADMIN', 'ROLE_ADMIN']);

/** 발행된 아티클만 보이는 일반 피드 */
const PublicArticleGrid = () => {
  const t = useT();

  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let ignore = false;
    setIsLoading(true);
    setHasError(false);

    getArticles()
      .then((data) => {
        if (!ignore) setArticles(data);
      })
      .catch(() => {
        if (!ignore) setHasError(true);
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, []);

  if (hasError) {
    return (
      <StateMessage
        title={t(
          "We couldn't load articles right now.",
          '지금은 아티클을 불러올 수 없어요.',
        )}
        description={t(
          'Please try again in a moment.',
          '잠시 후 다시 시도해 주세요.',
        )}
      />
    );
  }

  if (isLoading) return <SkeletonGrid />;

  if (articles.length === 0) {
    return (
      <StateMessage
        title={t('No articles yet.', '아직 아티클이 없어요.')}
        description={t(
          'New stories are on the way.',
          '새로운 이야기를 준비하고 있어요.',
        )}
      />
    );
  }

  return (
    <div className={GRID_CLASS}>
      {articles.map((article) => (
        <ArticleCard key={article.id} article={article} />
      ))}
    </div>
  );
};

/**
 * Article 피드.
 *
 * <p>JobBoard와 같은 방식으로, 같은 경로에서 역할에 따라 그리드만 바꿔 끼운다.
 * 어드민은 여기서 바로 상태 배지와 숨기기/삭제를 쓸 수 있다.
 */
const Article = () => {
  // user는 "로딩 중"과 "비로그인"이 모두 null이라 loading으로 구분해야 한다
  const { user, loading: isAuthLoading } = useAuth();
  const isAdmin = ADMIN_ROLES.has(String(user?.role ?? '').toUpperCase());

  return (
    <div className='w-full min-h-screen bg-grayscale-gray01'>
      <div className={CONTAINER_CLASS}>
        {isAuthLoading ? (
          <SkeletonGrid />
        ) : isAdmin ? (
          <AdminArticleGrid />
        ) : (
          <PublicArticleGrid />
        )}
      </div>
    </div>
  );
};

export default Article;
